/**
 * Brand palette for user face avatars. Eight pairs of background +
 * foreground (face features) drawn from the Replo brand colors. Light bgs use
 * a near-black face; saturated bgs use white — so the eyes/mouth always read.
 */
import { colors as latticeColors } from "../../styles/colors";

const FACE_DARK = "#1a1a1a";
const FACE_LIGHT = "#ffffff";

export type BrandAvatarColor = {
  background: string;
  foreground: string;
  /**
   * True for backgrounds so light they'd disappear against the surrounding
   * surface — render a thin border so the avatar still reads as an object.
   */
  needsBorder?: boolean;
};

export const BRAND_AVATAR_COLORS = [
  {
    background: latticeColors.brand.seashell,
    foreground: FACE_DARK,
    needsBorder: true,
  },
  { background: latticeColors.brand.yellow, foreground: FACE_DARK },
  { background: latticeColors.brand.lightGreen, foreground: FACE_DARK },
  { background: latticeColors.brand.darkGreen, foreground: FACE_LIGHT },
  { background: latticeColors.brand.lightPink, foreground: FACE_DARK },
  { background: latticeColors.brand.magenta, foreground: FACE_LIGHT },
  { background: latticeColors.brand.purple, foreground: FACE_LIGHT },
  { background: latticeColors.brand.blue, foreground: FACE_LIGHT },
] as const satisfies readonly BrandAvatarColor[];

/**
 * Deterministic brand palette pick from a stable seed (user id or email).
 * Same seed always maps to the same pair so a teammate's color stays
 * consistent across presence, members, and account surfaces.
 */
// Why: djb2-style char hash (* 31 mixes bits cheaply, `| 0` keeps it 32-bit);
// `% palette.length` distributes evenly across the 8 brand pairs.
export function getUserBrandAvatarColor(seed: string): BrandAvatarColor {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  }
  return BRAND_AVATAR_COLORS[Math.abs(hash) % BRAND_AVATAR_COLORS.length]!;
}
